import _ from 'lodash'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useStaticQuery, graphql } from 'gatsby'
import { useUser } from '../../vendor/react-omnitech-api'
import { useCart, useDineInMiniCart, useThemeConfig } from '../../hook'
import { useModal } from '../../hook/use-modal'
import useStyles from './dine-in-mini-cart-style'
import Button from '../button'
import CartLineItem from '../cart-line-item'
import SidePanel from '../side-panel'
import { isNullOrUndefined, groupAddonsWithQuantity } from '../../helpers'
import ClearIcon from '../../assets/icons/icon_clear.inline.svg'

const Item = (props) => {
  const {
    addons = [],
    inlineError,
    inlineErrors = [],
    title,
    quantity,
    onRemove,
    // onEdit,
  } = props
  const styles = useStyles()
  return (
    <div className={styles.item}>
      <p className={styles.itemTitle}>{`${quantity} x ${title}`}</p>
      {
        _.map(groupAddonsWithQuantity(addons), (addon) => (
          <p
            key={_.get(addon, 'identifierUuid')}
            className={styles.addon}
          >
            {`${_.get(addon, 'title')} x ${_.get(addon, 'quantity')}`}
          </p>
        ))
      }
      {
        !_.isEmpty(inlineError) && (
          <p className={styles.itemError}>{inlineError}</p>
        )
      }
      {
        _.map(inlineErrors, (error, index) => (
          <p key={`inline_error_${index}`} className={styles.itemError}>{error}</p>
        ))
      }
      <button
        className={styles.clearIconButton}
        type="button"
        onClick={onRemove}
      >
        <ClearIcon className={styles.clearIcon} alt="delete" />
      </button>
    </div>
  )
}

const DineInMiniCartView = () => {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            version
          }
        }
      }
    `,
  )
  // prepare hook
  const { t } = useTranslation()
  const {
    // cart,
    loading,
    miniCartOpen,
    // totalItems,
    skusUnavailableToCheckout,
    closeMiniCart,
    onRemoveCartLine,
    onPlaceOrder,
  } = useDineInMiniCart()

  const {
    dineInStagingCart,
  } = useCart()
  const {
    user,
  } = useUser()
  const { getConfig } = useThemeConfig()
  const modal = useModal()

  const isTester = _.includes(_.get(user, 'roles', []), 'tester')

  // Theme config
  const enableAppVersion = getConfig('config.ui.dineInMiniCart.enableAppVersion', false)
  const enableInlineErrorSkuCodes = getConfig('config.ui.dineInMiniCart.enableInlineErrorSkuCodes', false)
  const enableTopPlaceOrderButton = getConfig('config.ui.dineInMiniCart.enableTopPlaceOrderButton', false)

  // local variable
  const actions = _.get(dineInStagingCart, 'actions', [])

  const groupedCartLineProperties = _.map(
    _.filter(actions, ({ productAddonId }) => isNullOrUndefined(productAddonId)),
    (line) => ({
      ...line,
      addons: _.filter(
        actions,
        ({ productAddonId, groupUuid }) => !isNullOrUndefined(productAddonId) && _.isEqual(groupUuid, _.get(line, 'groupUuid')),
      ),
    }),
  )

  const cartLineItems = _.map(
    groupedCartLineProperties,
    (item) => {
      const notAllowedSkus = _.filter(skusUnavailableToCheckout, (sku) => (
        _.includes(
          [
            _.get(item, 'skuId'),
            ..._.map(_.get(item, 'addons', []), 'skuId'),
          ],
          sku.id,
        )
      ))
      const invalidKeys = _.uniq(_.flatMap(notAllowedSkus, 'validationErrors'))
      const showNotAllowedToCheckoutMessage = !_.isEmpty(invalidKeys)
      return {
        ...item,
        ...showNotAllowedToCheckoutMessage && {
          inlineErrors: isTester
            ? [`${t('ui.dineInMiniCart.itemUnavailable')} ::Tester only:: ${_.join(invalidKeys, ' | ')}`]
            : _.compact([
              ..._.map(invalidKeys, (context) => (
                t('ui.dineInMiniCart.itemUnavailable', { context })
              )),
              enableInlineErrorSkuCodes && `[${_.join(_.map(notAllowedSkus, 'code'))}]`,
            ]),
        },
      }
    },
  )

  const continueButtonDisabled = useMemo(() => (
    _.isEmpty(actions)
    || !_.isEmpty(skusUnavailableToCheckout)
    || loading
  ), [actions, skusUnavailableToCheckout, loading])

  /**
   * handleContinue
   * go to cart page
   */
  const handleContinue = () => {
    closeMiniCart()
    // FL: Show place order dialog
    modal.open({
      title: t('ui.dineInMiniCart.confirmationDialog.title'),
      message: t('ui.dineInMiniCart.confirmationDialog.description'),
      buttons: [
        {
          text: t('ui.dineInMiniCart.confirmationDialog.buttons.confirm'),
          onClick: () => {
            handlePlaceOrder()
          },
          disabled: loading,
        },
        {
          text: t('ui.dineInMiniCart.confirmationDialog.buttons.cancel'),
          onClick: () => {
            modal.close()
          },
          disabled: loading,
        },
      ],
    })
  }

  const handlePlaceOrder = async () => {
    try {
      await onPlaceOrder()
      // alert.show('Order Placed')
    } catch (error) {
      console.log('//// error ...', error)
    } finally {
      modal.close()
    }
  }

  // style
  const styles = useStyles({ miniCartOpen })

  const panelProps = {
    buttons: [
      {
        className: styles.button,
        text: t('ui.dineInMiniCart.buttons.continue'),
        onClick: handleContinue,
        disabled: continueButtonDisabled,
      },
    ],
    isOpen: miniCartOpen,
    onRequestClose: closeMiniCart,
    title: t([
      'ui.dineInMiniCart.title',
      'ui.miniCart.title',
    ], { context: 'dineIn' }),
  }

  return (
    <SidePanel
      {...panelProps}
      ContentTopComponent={enableTopPlaceOrderButton && (
        <div className={styles.topButton}>
          <Button
            className={styles.button}
            text={t('ui.dineInMiniCart.buttons.continue')}
            onClick={handleContinue}
            disabled={continueButtonDisabled}
          />
        </div>
      )}
    >
      <div className={styles.cartLines}>
        {
          loading
            ? (
              <>
                {
                  _.times(3, (index) => (
                    <CartLineItem key={index} placeholder />
                  ))
                }
              </>
            )
            : (
              <>
                {
                  _.map(cartLineItems, (cartLine) => (
                    <Item
                      key={_.get(cartLine, 'identifierUuid')}
                      // onEdit={() => { onItemEdit(cartLine) }}
                      onRemove={() => { onRemoveCartLine(cartLine) }}
                      {...cartLine}
                    />
                  ))
                }
              </>
            )
        }
      </div>
      {
        enableAppVersion && (
          <small className={styles.appVersion}>{site.siteMetadata.version}</small>
        )
      }
    </SidePanel>
  )
}

export default DineInMiniCartView
